import Graph from 'graphology';
import circlepack from 'graphology-layout/circlepack';
import forceAtlas2 from 'graphology-layout-forceatlas2';
import type { Node, Edge } from '@xyflow/react';
import type { NodePosition } from './layout-types';

const BARNES_HUT_NODE_COUNT = 150;
const MEDIUM_GRAPH_NODE_COUNT = 300;
const SMALL_GRAPH_ITERATIONS = 400;
const MEDIUM_GRAPH_ITERATIONS = 200;
const LARGE_GRAPH_ITERATIONS = 150;
const DEFAULT_NODE_SPACING = 320;

export function resolveForceAtlas2Run({ order }: { order: number }): {
  iterations: number;
  barnesHutOptimize: boolean;
} {
  const iterations =
    order > MEDIUM_GRAPH_NODE_COUNT
      ? LARGE_GRAPH_ITERATIONS
      : order > BARNES_HUT_NODE_COUNT
        ? MEDIUM_GRAPH_ITERATIONS
        : SMALL_GRAPH_ITERATIONS;

  return {
    iterations,
    barnesHutOptimize: order > BARNES_HUT_NODE_COUNT,
  };
}

function buildGraphologyGraph({
  nodes,
  edges,
}: {
  nodes: Node[];
  edges: Edge[];
}): Graph {
  const graph = new Graph();

  for (const node of nodes) {
    graph.mergeNode(node.id);
  }

  for (const edge of edges) {
    if (edge.source === edge.target) continue;
    if (!graph.hasNode(edge.source) || !graph.hasNode(edge.target)) continue;
    graph.mergeEdge(edge.source, edge.target);
  }

  return graph;
}

function scaleToPixelSpace({
  graph,
  nodeSpacing,
}: {
  graph: Graph;
  nodeSpacing: number;
}): Map<string, NodePosition> {
  const positions = new Map<string, NodePosition>();
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  graph.forEachNode((nodeId, attributes) => {
    const x = typeof attributes.x === 'number' ? attributes.x : 0;
    const y = typeof attributes.y === 'number' ? attributes.y : 0;

    positions.set(nodeId, { x, y });
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  });

  const width = maxX - minX;
  const height = maxY - minY;
  const extent = Math.max(width, height);

  if (!Number.isFinite(extent) || extent === 0) {
    return positions;
  }

  const targetExtent = nodeSpacing * Math.sqrt(graph.order);
  const scale = targetExtent / extent;
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;

  for (const [nodeId, position] of positions) {
    positions.set(nodeId, {
      x: (position.x - centerX) * scale,
      y: (position.y - centerY) * scale,
    });
  }

  return positions;
}

export function getForceAtlas2Layout({
  nodes,
  edges,
  nodeSpacing = DEFAULT_NODE_SPACING,
}: {
  nodes: Node[];
  edges: Edge[];
  nodeSpacing?: number;
}): Map<string, NodePosition> {
  if (nodes.length === 0) {
    return new Map();
  }

  if (nodes.length === 1) {
    return new Map([[nodes[0].id, { x: 0, y: 0 }]]);
  }

  const graph = buildGraphologyGraph({ nodes, edges });

  const run = resolveForceAtlas2Run({ order: graph.order });

  circlepack.assign(graph);
  forceAtlas2.assign(graph, {
    iterations: run.iterations,
    settings: {
      ...forceAtlas2.inferSettings(graph),
      barnesHutOptimize: run.barnesHutOptimize,
    },
  });

  return scaleToPixelSpace({ graph, nodeSpacing });
}
